import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useCopy } from '@floorfile/i18n'
import { Button } from '@floorfile/ui'
import { isOfflineChunkError } from '@/lib/lazy-route'
import { componentsCopy } from './copy'

/**
 * Keeps a crashing feature page from taking down the whole app shell
 * (audit RUN-12).
 *
 * `errorElement` was declared on only the two ROOT route objects, and none of
 * the ~90 child routes — and there was no error-boundary component anywhere in
 * the repo. React Router therefore bubbled any child render error all the way
 * to `/`, replacing `<AppShell/>` wholesale: sidebar, header, notification
 * bell, and facility switcher all unmounted, leaving a hard reload as the only
 * way back. On a floor tablet that is the difference between "this screen is
 * broken, use another one" and "the app is gone".
 *
 * Wrapping the shell's `<Outlet/>` scopes the failure to the page content and
 * keeps navigation alive, so the operator can move somewhere useful.
 */
interface Props {
  children: ReactNode
  /** Changing this resets the boundary — pass the route key so navigating away
   * from a broken page clears the error instead of pinning it. */
  resetKey?: string
}

interface State {
  error: Error | undefined
}

/**
 * The fallback is a FUNCTION component so it can call `useCopy`.
 *
 * An error boundary still has no hook equivalent, so the class below cannot
 * read the catalogue itself. `getCopy(…, getActiveLocale())` inside `render()`
 * would compile, but a class does not subscribe to the translation registry —
 * the fallback would keep rendering whichever locale was active when it
 * mounted, and a boundary is by definition mounted long before it renders.
 */
function FeatureErrorFallback({ onRetry }: { onRetry: () => void }) {
  const c = useCopy(componentsCopy).featureError
  return (
    <div role="alert" className="mx-auto max-w-lg p-6">
      <h2 className="text-lg font-semibold text-foreground">{c.title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
      <Button onClick={onRetry} className="mt-4">
        {c.retry}
      </Button>
    </div>
  )
}

/**
 * The offline variant, and the reason this boundary has to tell the two apart.
 *
 * Since the precache became an allow-list (`vite.config.ts`, 2026-08-03), every
 * screen outside the eight offline-first ones is downloaded on first open. In a
 * dead zone, opening one for the first time lands HERE — this boundary sits
 * around the shell's `<Outlet/>`, so it catches the failed route chunk before
 * React Router's `errorElement` ever sees it. Verified in the browser against a
 * stopped server: `/settings/company` reached this fallback, not `RouteErrorCard`.
 *
 * "This screen hit a problem" is wrong for that: nothing is broken, the screen
 * is simply not on the device, and "Try again" cannot succeed until there is a
 * connection. Saying so is the difference between a user who waits and a user
 * who files a bug.
 */
function OfflineScreenFallback() {
  const c = useCopy(componentsCopy).featureError
  return (
    <div role="alert" className="mx-auto max-w-lg p-6">
      <h2 className="text-lg font-semibold text-foreground">{c.offlineTitle}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{c.offlineBody}</p>
    </div>
  )
}

export class FeatureErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: undefined })
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A screen that is not on the device yet is an expected outcome of the
    // precache allow-list, not a fault — `warn`, the same level `ChartFrame`
    // uses for the same reason. Anything else keeps its error report.
    if (isOfflineChunkError(error)) {
      console.warn('Route chunk unavailable offline:', error)
      return
    }
    // Surfaced through the platform's normal client error path; the shell
    // stays interactive either way.
    console.error('Feature page crashed:', error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    if (isOfflineChunkError(this.state.error)) return <OfflineScreenFallback />
    return <FeatureErrorFallback onRetry={() => this.setState({ error: undefined })} />
  }
}
